/**
 * lib/scrollToResults.js (v2.71.72)
 * ---------------------------------
 * ONE global helper for the "apply filter / submit search → smoothly
 * bring results into view" UX. No React state, no context, no event
 * bus — just a function any handler can call.
 *
 * Convention (one-line opt-in per page):
 *
 *     <section data-results-anchor="properties"> … </section>
 *
 * The first matching `[data-results-anchor]` element on the page is
 * the scroll target. Pages that pre-existed our convention with
 * `data-testid="listings-section"` are also picked up as a fallback,
 * so we don't have to touch every legacy page in one pass.
 *
 * Behaviour notes:
 *   • Computes sticky-header offset by measuring whatever's docked
 *     at top (header.site-header-sticky → 64px fallback).
 *   • Respects `prefers-reduced-motion` — uses `auto` (instant) scroll
 *     instead of smooth for users who opted out.
 *   • Debounced 300 ms: rapid pill-swap → single scroll, not five.
 *   • Bounded by `requestAnimationFrame` so the target's final
 *     position (after the filter change kicks in layout) is what we
 *     scroll to, not the pre-render position.
 *   • Pure DOM, no animation library, no portals, no body-scroll
 *     locking — works equally well on iOS Safari + Android Chrome.
 */

const TARGET_SELECTORS = [
  "[data-results-anchor]",
  "[data-testid='listings-section']",
];

// Single debounce token shared across the app — prevents double-scroll
// when two handlers fire in the same tick (e.g. setFilters + setSort).
let lastCallAt = 0;
const DEBOUNCE_MS = 300;

/**
 * Resolve the sticky-header height so we don't scroll the results
 * BEHIND the header. Reads `offsetHeight` of the docked element.
 * Falls back to 64 (the platform's standard mobile header height).
 */
function getStickyOffset() {
  if (typeof document === "undefined") return 56;
  const header =
    document.querySelector("header.site-header-sticky") ||
    document.querySelector("[data-site-header]") ||
    document.querySelector("header");
  if (!header) return 56;
  const h = header.offsetHeight || 0;
  return h + 8; // small breathing gap
}

function findTarget(selectors) {
  if (typeof document === "undefined") return null;
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) return el;
  }
  return null;
}

/**
 * scrollToResults(opts?)
 *
 * @param {object} [opts]
 * @param {string} [opts.anchor] - Override the anchor name. If supplied,
 *   targets `[data-results-anchor='${anchor}']` first, then falls back
 *   to the default chain.
 * @param {number} [opts.delay=0] - Optional millisecond delay before
 *   the scroll fires. Useful if you know your results need a frame or
 *   two to render before measuring.
 * @param {ScrollBehavior} [opts.behavior='smooth']
 * @param {boolean} [opts.force=false] - Skip the global 300 ms debounce.
 */
export function scrollToResults(opts = {}) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (!opts.force && now - lastCallAt < DEBOUNCE_MS) return;
  lastCallAt = now;

  const reduce =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const behavior = reduce ? "auto" : opts.behavior || "smooth";

  const selectors = opts.anchor
    ? [`[data-results-anchor='${opts.anchor}']`, ...TARGET_SELECTORS]
    : TARGET_SELECTORS;

  const doScroll = () => {
    const target = findTarget(selectors);
    if (!target) return;
    // v2.87 Phase 3a — Smarter "skip when near results" gate.
    // If a filter strip is already visible in the viewport, treat the
    // user as "already at the filter area" and DON'T jump them around.
    // Only scroll when the user is far above the filter strip (e.g.
    // clicked an intent chip from the hero / top nav).
    // Callers can override with `{ force: true }` (e.g. mobile Apply
    // button which always scrolls past the closing sheet).
    if (!opts.force) {
      const strip = document.querySelector(
        "[data-results-anchor='filter-strip'], [data-testid='fp-root'], [data-testid='pfp-root']"
      );
      if (strip) {
        const sr = strip.getBoundingClientRect();
        // strip's bottom is on-screen AND its top is below the sticky offset → already near it
        const offset = getStickyOffset();
        if (sr.bottom > offset && sr.top < window.innerHeight) {
          return; // strip in viewport, don't scroll
        }
      }
    }
    const rect = target.getBoundingClientRect();
    const top = window.scrollY + rect.top - getStickyOffset();
    if (top < 0) return; // already above target — don't scroll up
    // Skip if the target's top is already in the top third of viewport
    if (rect.top > 0 && rect.top < window.innerHeight * 0.35) return;

    const startY = window.scrollY;
    window.scrollTo({ top, behavior });

    // v2.71.72 — Resilience fallback. Some browsers / WebViews / headless
    // contexts silently no-op `behavior: 'smooth'` (notably any context
    // where `prefers-reduced-motion: reduce` is forced, plus older
    // Chromium versions). If we asked for smooth but the page hasn't
    // budged after 400 ms, switch to instant so the user still sees the
    // results — UX trumps animation.
    if (behavior === "smooth") {
      setTimeout(() => {
        const progressed = Math.abs(window.scrollY - startY) > 30;
        if (!progressed) {
          window.scrollTo({ top, behavior: "auto" });
        }
      }, 400);
    }
  };

  // v2.71.72 — Default delay 80ms.
  // Many call sites (mobile filter Sheet apply) fire scrollToResults()
  // synchronously while a Radix Dialog/Sheet is unmounting. Radix locks
  // `document.body.style.overflow` while open and releases it in a useEffect
  // cleanup — which runs AFTER the current React commit. If we scroll
  // immediately, the scrollTo gets eaten by the still-locked body. A small
  // delay defers the scroll past Radix's cleanup. Callers can override
  // with `{ delay: 0 }` if they know there's no overlay teardown.
  const effectiveDelay = opts.delay !== undefined ? opts.delay : 80;
  if (effectiveDelay > 0) {
    setTimeout(() => requestAnimationFrame(doScroll), effectiveDelay);
  } else {
    requestAnimationFrame(doScroll);
  }
}

/**
 * Tiny convenience: a stable function reference for callers that want
 * to pass the handler as a prop (avoid re-creating closures). Same
 * behaviour as the named export.
 */
export default scrollToResults;
